import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface UpcomingTourStat {
  count: number;
  firstDate: number;
}

const hashIds = (ids: string[]) => {
  let h = 0;
  for (const id of ids) {
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return `${ids.length}:${h}`;
};

async function fetchUpcomingStats(tourIds: string[]) {
  const today = new Date();
  const withinDays = new Date(today);
  withinDays.setDate(today.getDate() + 120);

  const CHUNK_SIZE = 100;
  const idChunks: string[][] = [];
  for (let i = 0; i < tourIds.length; i += CHUNK_SIZE) {
    idChunks.push(tourIds.slice(i, i + CHUNK_SIZE));
  }

  const chunkResults = await Promise.all(
    idChunks.map((chunk) =>
      supabase
        .from('concerts')
        .select('tour_id, date')
        .gte('date', today.toISOString().split('T')[0])
        .lte('date', withinDays.toISOString().split('T')[0])
        .in('tour_id', chunk)
    )
  );

  const stats: Record<string, UpcomingTourStat> = {};
  for (const c of chunkResults.flatMap((r) => r.data || [])) {
    const id = (c as any).tour_id as string | null;
    if (!id) continue;
    const d = new Date((c as any).date as string).getTime();
    const prev = stats[id] || { count: 0, firstDate: Number.POSITIVE_INFINITY };
    stats[id] = { count: prev.count + 1, firstDate: Math.min(prev.firstDate, d) };
  }
  return stats;
}

/**
 * 120-day upcoming-concert counts for the tours Home already loaded.
 * Cached so a Home remount reuses the previous result instead of refetching.
 */
export const useHomeUpcomingTourStats = (tourIds: string[]) =>
  useQuery({
    queryKey: ['home', 'upcoming-tour-stats', hashIds(tourIds)],
    queryFn: () => fetchUpcomingStats(tourIds),
    enabled: tourIds.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
